/**
 * กฎว่าใครลบรายการไหนได้บ้าง
 *
 * อยู่ใน shared เพราะทั้งสองฝั่งต้องใช้กฎเดียวกัน
 *   ฝั่ง server  ใช้บังคับจริง เป็นด่านเดียวที่นับ
 *   ฝั่ง UI      ใช้ตัดสินว่าจะแสดงปุ่มลบไหม และบอกเหตุผลล่วงหน้าเมื่อลบไม่ได้
 *
 * ⚠️ การซ่อนปุ่มฝั่ง UI ไม่ใช่การควบคุมสิทธิ์ ถ้าไฟล์นี้ถูกเรียกจากฝั่ง UI อย่างเดียว
 * แล้วลืมเรียกฝั่ง server ระบบจะเปิดช่องให้ลบข้ามสิทธิ์ได้ทันที
 * ฝั่ง server เรียกฟังก์ชันนี้ใน voidCapture() เสมอ ไม่ว่าจะมาจาก route ไหน
 *
 * เหตุผลที่แยกเป็นไฟล์แทนที่จะเขียนซ้ำสองที่: ถ้าปุ่มกับกฎจริงไม่ตรงกัน
 * ผู้ใช้จะเห็นปุ่มแล้วกดไม่ได้ (น่ารำคาญ) หรือไม่เห็นปุ่มทั้งที่ทำได้ (ใช้งานไม่ได้จริง)
 */
import { ERROR_CODES, type ErrorCode } from './errors';
import type { AuthenticatedUser, Capture } from './types';
import { minutesSince } from './time';

export type VoidDecision = { allowed: true } | { allowed: false; code: ErrorCode; reason: string };

/** ข้อมูลของรายการเท่าที่การตัดสินใจนี้ต้องใช้ */
export type VoidableCapture = Pick<Capture, 'createdBy' | 'areaCode' | 'status' | 'createdAt'>;

/**
 * ผู้ใช้เท่าที่การตัดสินใจนี้ต้องรู้
 *
 * ตั้งใจไม่รับ AuthenticatedUser เต็มก้อน เพราะฝั่ง UI มีข้อมูลผู้ใช้แค่ไม่กี่ฟิลด์
 * (ที่ /api/me ส่งมา) การบังคับให้ครบทุกฟิลด์จะทำให้ UI เรียกกฎเดียวกันนี้ไม่ได้
 * แล้วต้องไปเขียนกฎซ้ำ ซึ่งเป็นสิ่งที่ไฟล์นี้มีไว้ป้องกัน
 */
export type DecidingUser = Pick<AuthenticatedUser, 'id' | 'role' | 'areaCode'>;

export interface VoidPolicy {
  /** เจ้าของลบเองได้ภายในกี่นาทีนับจากสร้างรายการ */
  ownerEditWindowMinutes: number;
}

/**
 * ตัดสินว่าผู้ใช้คนนี้ลบรายการนี้ได้หรือไม่
 *
 * สรุปกฎ
 *   ผู้ดูแลระบบ     ลบได้ทุกรายการ
 *   หัวหน้างาน      ลบได้เฉพาะในพื้นที่ที่รับผิดชอบ (ไม่กำหนดพื้นที่ = ทุกพื้นที่)
 *   เจ้าหน้าที่      ลบได้เฉพาะรายการที่ตนบันทึก และต้องอยู่ในกรอบเวลาที่กำหนด
 *
 * กรอบเวลาของเจ้าหน้าที่ใช้ค่าเดียวกับการแก้ไขรายการ (ownerEditWindowMinutes)
 * เพื่อไม่ให้มีสองกฎที่ต้องจำ และเพื่อไม่ให้ย้อนไปลบงานเก่าเป็นเดือนได้เอง
 * ถ้าพ้นกรอบเวลาแล้วยังต้องลบจริง ให้หัวหน้างานหรือผู้ดูแลระบบเป็นผู้ลบ
 */
export function canVoidCapture(
  user: DecidingUser,
  capture: VoidableCapture,
  policy: VoidPolicy,
  now = Date.now(),
): VoidDecision {
  if (capture.status === 'void') {
    return {
      allowed: false,
      code: ERROR_CODES.INVALID_STATUS_TRANSITION,
      reason: 'รายการนี้ถูกลบไปแล้ว',
    };
  }

  if (user.role === 'administrator') return { allowed: true };

  if (user.role === 'supervisor') {
    if (!user.areaCode || capture.areaCode === user.areaCode) return { allowed: true };
    return {
      allowed: false,
      code: ERROR_CODES.AREA_FORBIDDEN,
      reason: 'รายการนี้อยู่นอกพื้นที่ที่คุณรับผิดชอบ',
    };
  }

  // เจ้าหน้าที่ภาคสนาม
  if (capture.createdBy !== user.id) {
    /*
     * ตอบ NOT_FOUND ไม่ใช่ FORBIDDEN เหมือนกับตอนอ่าน
     * เพื่อไม่ให้ใช้ปุ่มลบไล่ตรวจว่ามีรายการของคนอื่นอยู่จริงหรือไม่
     */
    return {
      allowed: false,
      code: ERROR_CODES.NOT_FOUND,
      reason: 'ไม่พบรายการที่ต้องการ',
    };
  }

  const elapsed = minutesSince(capture.createdAt, now);
  if (elapsed > policy.ownerEditWindowMinutes) {
    return {
      allowed: false,
      code: ERROR_CODES.EDIT_WINDOW_EXPIRED,
      reason: `ลบเองได้ภายใน ${policy.ownerEditWindowMinutes} นาทีหลังบันทึกเท่านั้น กรุณาแจ้งหัวหน้างาน`,
    };
  }

  return { allowed: true };
}

/** เหลือเวลาให้เจ้าของลบเองอีกกี่นาที — ใช้แสดงบน UI, 0 = หมดเวลาแล้ว */
export function minutesLeftToVoid(
  user: DecidingUser,
  capture: VoidableCapture,
  policy: VoidPolicy,
  now = Date.now(),
): number | null {
  if (user.role !== 'field_officer') return null; // ไม่มีกรอบเวลา
  const left = policy.ownerEditWindowMinutes - minutesSince(capture.createdAt, now);
  return left > 0 ? Math.ceil(left) : 0;
}
